import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Utensils, DollarSign } from 'lucide-react';
import { addDishesToMenu, removeDishFromMenu } from '../../../services/restaurantApi';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const MenuDetailsModal = ({ isOpen, onClose, menu, allDishes, onMenuUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [showAddDishes, setShowAddDishes] = useState(false);
  const [selectedDishIds, setSelectedDishIds] = useState([]);

  // selectedDishIds = plats à ajouter dans le popup "Ajouter des plats" (ne pas pré-remplir avec les plats déjà dans le menu)
  useEffect(() => {
    if (isOpen && !showAddDishes) {
      setSelectedDishIds([]);
    }
  }, [menu?.id, isOpen, showAddDishes]);

  if (!isOpen || !menu) return null;

  const handleAddDishes = async () => {
    if (selectedDishIds.length === 0) return;

    try {
      setLoading(true);
      await addDishesToMenu(menu.id, selectedDishIds);
      setSelectedDishIds([]);
      setShowAddDishes(false);
      await onMenuUpdate(); // Recharger les données
    } catch (error) {
      console.error('Erreur lors de l\'ajout des plats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDish = async (dishId) => {
    try {
      setLoading(true);
      await removeDishFromMenu(menu.id, dishId);
      onMenuUpdate(); // Recharger les données
    } catch (error) {
      console.error('Erreur lors de la suppression du plat:', error);
    } finally {
      setLoading(false);
    }
  };

  const availableDishes = allDishes.filter(dish =>
    !menu.dishes?.some(menuDish => menuDish.id === dish.id)
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
      <div style={{ background: '#FCFBF5', borderRadius: '28px', maxWidth: '760px', width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', padding: '22px 26px', borderBottom: '1px solid rgba(5,16,4,0.08)', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>{menu.name}</h2>
            {menu.description && <p style={{ margin: '6px 0 0', fontSize: '13.5px', color: 'rgba(5,16,4,0.62)' }}>{menu.description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            style={{ flexShrink: 0, border: 'none', cursor: 'pointer', width: '38px', height: '38px', borderRadius: '999px', background: 'transparent', color: 'rgba(5,16,4,0.4)', display: 'grid', placeItems: 'center' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(5,16,4,0.06)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '26px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '22px' }}>
          {/* Statistiques du menu */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px' }}>
            <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px' }}>
              <span className="flex items-center gap-2" style={{ color: PRIMARY }}>
                <Utensils size={16} />
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Plats</span>
              </span>
              <p style={{ margin: '6px 0 0', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px' }}>{menu.dishes ? menu.dishes.length : 0}</p>
            </div>
            <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px' }}>
              <span className="flex items-center gap-2" style={{ color: PRIMARY }}>
                <DollarSign size={16} />
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Prix total</span>
              </span>
              <p style={{ margin: '6px 0 0', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px' }}>
                {formatCurrency(menu.dishes ? menu.dishes.reduce((sum, dish) => sum + (dish.price || 0), 0) : 0)}
              </p>
            </div>
            <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px' }}>
              <span className="flex items-center gap-2" style={{ color: TERRA }}>
                <Utensils size={16} />
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Catégories</span>
              </span>
              <p style={{ margin: '6px 0 0', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px' }}>
                {new Set(menu.dishes?.map(dish => dish.category_id) || []).size}
              </p>
            </div>
          </div>

          {/* Plats du menu */}
          <div>
            <div className="flex items-center justify-between" style={{ marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '18px' }}>Plats du menu</h3>
              <button
                onClick={() => {
                  setSelectedDishIds([]);
                  setShowAddDishes(true);
                }}
                className="inline-flex items-center gap-2"
                style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13.5px', minHeight: '40px', padding: '0 18px', borderRadius: '999px' }}
              >
                <Plus size={15} />
                Ajouter des plats
              </button>
            </div>

            {menu.dishes && menu.dishes.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
                {menu.dishes.map((dish) => (
                  <div key={dish.id} style={{ border: '1px solid rgba(5,16,4,0.1)', borderRadius: '20px', padding: '16px' }}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1" style={{ minWidth: 0 }}>
                        <h4 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '16px' }}>{dish.name}</h4>
                        {dish.description && <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'rgba(5,16,4,0.62)' }} className="line-clamp-2">{dish.description}</p>}
                        <div className="flex items-center gap-3" style={{ marginTop: '8px', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>
                          <span>{formatCurrency(dish.price)}</span>
                          <span className="flex items-center gap-1">
                            <Utensils size={11} />
                            {dish.category_name || 'Non catégorisé'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveDish(dish.id)}
                        disabled={loading}
                        style={{ flexShrink: 0, border: 'none', cursor: 'pointer', background: 'transparent', color: '#A32C0F', width: '32px', height: '32px', borderRadius: '999px', display: 'grid', placeItems: 'center', opacity: loading ? 0.5 : 1 }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF0EA'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 16px', color: 'rgba(5,16,4,0.5)' }}>
                <Utensils size={40} style={{ margin: '0 auto 12px', color: 'rgba(5,16,4,0.18)' }} />
                <p style={{ margin: 0, fontSize: '14px' }}>Aucun plat dans ce menu</p>
                <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Cliquez sur "Ajouter des plats" pour commencer</p>
              </div>
            )}
          </div>

          {/* Modal d'ajout de plats */}
          {showAddDishes && (
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
              <div style={{ background: '#FCFBF5', borderRadius: '28px', maxWidth: '600px', width: '100%', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
                <div className="flex items-center justify-between" style={{ padding: '20px 24px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
                  <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '18px' }}>Ajouter des plats au menu</h3>
                  <button
                    onClick={() => setShowAddDishes(false)}
                    style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.4)', width: '36px', height: '36px', borderRadius: '999px', display: 'grid', placeItems: 'center' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(5,16,4,0.06)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <X size={16} />
                  </button>
                </div>

                <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {availableDishes.length > 0 ? (
                    availableDishes.map((dish) => {
                      const selected = selectedDishIds.includes(dish.id);
                      return (
                        <div
                          key={dish.id}
                          onClick={() => {
                            setSelectedDishIds(prev =>
                              prev.includes(dish.id)
                                ? prev.filter(id => id !== dish.id)
                                : [...prev, dish.id]
                            );
                          }}
                          className="flex items-center gap-3"
                          style={{
                            cursor: 'pointer', borderRadius: '16px', padding: '14px',
                            border: selected ? `2px solid ${PRIMARY}` : '1px solid rgba(5,16,4,0.1)',
                            background: selected ? '#FBF9D9' : 'transparent',
                          }}
                        >
                          <span style={{ flex: 'none', width: '22px', height: '22px', borderRadius: '7px', display: 'grid', placeItems: 'center', fontSize: '12px', fontWeight: 700, background: selected ? PRIMARY : 'transparent', color: selected ? '#FBF9D9' : 'transparent', border: selected ? 'none' : '2px solid rgba(5,16,4,0.22)' }}>✓</span>
                          <div className="flex-1" style={{ minWidth: 0 }}>
                            <h4 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '15px' }} className="truncate">{dish.name}</h4>
                            <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'rgba(5,16,4,0.6)' }} className="truncate">{dish.description}</p>
                            <div className="flex items-center gap-3" style={{ marginTop: '4px', fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.55)' }}>
                              <span>{formatCurrency(dish.price)}</span>
                              <span>{dish.category_name || 'Non catégorisé'}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 16px', color: 'rgba(5,16,4,0.5)' }}>
                      <Utensils size={40} style={{ margin: '0 auto 12px', color: 'rgba(5,16,4,0.18)' }} />
                      <p style={{ margin: 0, fontSize: '14px' }}>Tous les plats sont déjà dans ce menu</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2.5" style={{ padding: '18px 24px', borderTop: '1px solid rgba(5,16,4,0.08)' }}>
                  <button
                    onClick={() => setShowAddDishes(false)}
                    style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 20px', borderRadius: '999px' }}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleAddDishes}
                    disabled={selectedDishIds.length === 0 || loading}
                    style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 22px', borderRadius: '999px', opacity: (selectedDishIds.length === 0 || loading) ? 0.6 : 1 }}
                  >
                    {loading ? 'Ajout en cours...' : `Ajouter ${selectedDishIds.length} plat(s)`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuDetailsModal;
